import { useQuery } from '@tanstack/vue-query'
import type { MaybeRefOrGetter } from 'vue'
import { editAssignmentsApi, editAssignmentsQueryKeys } from '@/api/resources/edit-assignments'
import type { PendingEditAssignmentsResponse } from '@/api/resources/edit-assignments'

const EMPTY: PendingEditAssignmentsResponse = {
    assignments: [],
    window_hours: 0,
    release_enabled: false,
}

/**
 * Edits routed back to the signed-in editor that they haven't confirmed yet.
 *
 * Shared cache on purpose: the app-wide prompt and the proofing-room banner both read this one
 * query, so confirming in either place updates both. The endpoint is scoped to the caller's own
 * assignments, so it is safe to run for every signed-in user — non-editors get an empty list.
 */
export function usePendingEditAssignments(enabled: MaybeRefOrGetter<boolean> = true) {
    return useQuery({
        queryKey: editAssignmentsQueryKeys.pending(),
        queryFn: async () => (await editAssignmentsApi.pending()).data?.data ?? EMPTY,
        // The window is measured in hours, so there is nothing to gain from polling. One fetch per
        // page load, plus the refetch the confirmation mutation triggers.
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        retry: false,
        enabled,
    })
}
